/**
 * useAgentAPI Hook
 * 
 * React hook for managing the multi-agent system.
 * Provides reactive state and methods for agent transitions.
 * 
 * Supports two authentication modes:
 * 1. Legacy mode: Direct API key (less secure)
 * 2. Ephemeral token mode: Backend URL for secure token generation (recommended)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GenAILiveClient } from '../lib/genai-live-client';
import { AudioStreamer } from '../lib/audio-streamer';
import { audioContext } from '../lib/utils';
import VolMeterWorklet from '../lib/worklets/vol-meter';
import { logger } from '../utils/logger';
import { fetchSessionConfig, getCachedWebSocketClient } from '../lib/token-service';
import { SessionConfigResponse } from '../types';
import {
  AgentManager,
  createAgentManager,
  createAllAgents,
  getInitialAgentId,
  AgentManagerState,
  AgentContext,
  ToolCallResult,
  VoiceGender,
  TransitionData,
} from '../agents';

export interface UseAgentAPIOptions {
  // Legacy mode: Direct API key (not recommended for production)
  apiKey?: string;
  // Azure OpenAI endpoint (required for legacy mode)
  endpoint?: string;
  // Azure OpenAI deployment (required for legacy mode)
  deployment?: string;
  // Azure OpenAI API version (required for legacy mode)
  apiVersion?: string;
  // Secure mode: Backend URL for session config generation (recommended)
  backendUrl?: string;
}

export interface UseAgentAPIConfig {
  userData?: {
    username: string;
    mobileNumber: string;
  };
  voiceGender?: VoiceGender;
  userLocation?: {
    lat: number;
    lng: number;
    address?: string;
  };
}

export interface UseAgentAPIResult {
  // State
  connected: boolean;
  currentAgentId: string | null;
  context: AgentContext | null;
  volume: number;
  isTransitioning: boolean;
  
  // Methods
  connect: (config: UseAgentAPIConfig) => Promise<void>;
  disconnect: () => Promise<void>;
  stopAudio: () => void;
  send: (message: string, turnComplete?: boolean) => void;
  sendRealtimeInput: (chunks: Array<{ mimeType: string; data: string }>) => void;
  updateTransitionData: (data: Partial<TransitionData>) => void;
  transitionTo: (agentId: string, reason: string, data?: Partial<TransitionData>) => Promise<boolean>;
  
  // Agent manager for advanced use
  manager: AgentManager | null;
  
  // Client for tool call handling
  client: GenAILiveClient | null;
  
  // Audio streamer
  audioStreamer: AudioStreamer | null;
}

export function useAgentAPI(options: UseAgentAPIOptions): UseAgentAPIResult {
  // Validate options
  if (!options.apiKey && !options.backendUrl) {
    throw new Error('Either apiKey or backendUrl must be provided to useAgentAPI');
  }
  
  const useEphemeralTokens = Boolean(options.backendUrl);
  
  // Core state
  const [connected, setConnected] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null);
  const [context, setContext] = useState<AgentContext | null>(null);
  const [volume, setVolume] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Refs for stable instances
  const clientRef = useRef<GenAILiveClient | null>(null);
  const managerRef = useRef<AgentManager | null>(null);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const configRef = useRef<UseAgentAPIConfig | null>(null);
  
  // Initialize client for legacy mode only
  useEffect(() => {
    if (!useEphemeralTokens && options.apiKey) {
      // Legacy mode: Create client immediately with API key
      if (clientRef.current) {
        clientRef.current.disconnect();
      }
      clientRef.current = new GenAILiveClient({
        endpoint: options.endpoint || '',
        apiKey: options.apiKey,
        deployment: options.deployment || 'gpt-realtime-mini',
        apiVersion: options.apiVersion || '2025-12-15',
      });
      logger.connection('[useAgentAPI] Client created with API key (legacy mode)');
    }
    
    return () => {
      clientRef.current?.disconnect();
    };
  }, [options.apiKey, options.endpoint, options.deployment, options.apiVersion, useEphemeralTokens]);
  
  // Setup audio streamer
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: 'audio-out' }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>('vumeter-out', VolMeterWorklet, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            logger.audio('[useAgentAPI] Audio worklet initialized');
          });
      });
    }
    
    // Cleanup: destroy audio streamer on unmount
    return () => {
      if (audioStreamerRef.current) {
        logger.audio('[useAgentAPI] Cleaning up audio streamer');
        audioStreamerRef.current.destroy();
        audioStreamerRef.current = null;
      }
    };
  }, []);
  
  /**
   * Setup audio handling from client
   */
  const setupAudioHandlers = useCallback((client: GenAILiveClient) => {
    // Clear any old listeners to prevent accumulation
    client.removeAllListeners('audio');
    client.removeAllListeners('interrupted');
    
    // Setup audio handling
    client.on('audio', (data: ArrayBuffer) => {
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    });
    
    client.on('interrupted', () => {
      audioStreamerRef.current?.stop();
    });
  }, []);
  
  /**
   * Connect and start the agent system (Ephemeral Token Mode)
   */
  const connectWithTokens = useCallback(async (config: UseAgentAPIConfig) => {
    logger.connection('[useAgentAPI] Starting connection with ephemeral tokens');
    
    configRef.current = config;
    const voiceGender = config.voiceGender || 'male';
    const initialAgentId = getInitialAgentId();
    
    // Reset audio streamer for fresh playback
    if (audioStreamerRef.current) {
      audioStreamerRef.current.stop();
    }
    
    try {
      // Try to use pre-connected WebSocket client first (INSTANT)
      const cachedConnection = getCachedWebSocketClient();
      let model: string;
      
      if (cachedConnection) {
        logger.connection('[useAgentAPI] ⚡⚡⚡ Using pre-connected WebSocket for INSTANT streaming!');
        clientRef.current = cachedConnection.client;
        model = cachedConnection.model;
        
        // Setup audio handlers on the already-connected client
        setupAudioHandlers(clientRef.current);
        
        logger.connection('[useAgentAPI] ✅ Connection ready - NO DELAY!');
      } else {
        // Fallback: Normal flow - fetch session config and establish connection
        logger.connection('[useAgentAPI] No pre-connected client, establishing new connection...');
        logger.connection('[useAgentAPI] Fetching initial session config for agent:', initialAgentId);
        
        const sessionData: SessionConfigResponse = await fetchSessionConfig(
          options.backendUrl!,
          initialAgentId,
          voiceGender
        );
        
        logger.connection('[useAgentAPI] Session config received, deployment:', sessionData.deployment);
        model = sessionData.deployment;
        
        // Create client with Azure OpenAI config (no API key for proxy mode)
        clientRef.current = new GenAILiveClient({
          endpoint: sessionData.endpoint,
          deployment: sessionData.deployment,
          apiVersion: sessionData.api_version,
        });
        
        // Setup audio handlers
        setupAudioHandlers(clientRef.current);
        
        // Connect via backend proxy (API key stays server-side)
        logger.connection('[useAgentAPI] Establishing WebSocket connection via proxy...');
        await clientRef.current.connectViaProxy(options.backendUrl!, initialAgentId, voiceGender);
        logger.connection('[useAgentAPI] ✅ WebSocket connected via proxy');
      }
      
      // Create all agents (for local tool handling)
      const agents = createAllAgents(voiceGender);
      
      // Create manager in ephemeral token mode
      const manager = createAgentManager({
        client: clientRef.current,
        model: model,
        initialAgentId,
        agents,
        userData: config.userData,
        voiceGender,
        userLocation: config.userLocation,
        useEphemeralTokens: true,
        backendUrl: options.backendUrl,
      });
      
      managerRef.current = manager;
      
      // Setup manager event listeners
      setupManagerEvents(manager);
      
      // Start the agent system
      await manager.start();
      
    } catch (error) {
      logger.error('[useAgentAPI] Failed to connect with ephemeral tokens:', error);
      throw error;
    }
  }, [options.backendUrl, setupAudioHandlers]);
  
  /**
   * Connect and start the agent system (Legacy API Key Mode)
   */
  const connectWithApiKey = useCallback(async (config: UseAgentAPIConfig) => {
    if (!clientRef.current) {
      throw new Error('Client not initialized');
    }
    
    logger.connection('[useAgentAPI] Starting connection with API key (legacy mode)');
    
    // Clear any old listeners to prevent accumulation
    setupAudioHandlers(clientRef.current);
    
    // Reset audio streamer for fresh playback
    if (audioStreamerRef.current) {
      audioStreamerRef.current.stop();
    }
    
    configRef.current = config;
    const voiceGender = config.voiceGender || 'male';
    
    logger.connection('[useAgentAPI] Creating agent system with voice:', voiceGender);
    
    // Create all agents
    const agents = createAllAgents(voiceGender);
    
    // Create manager (fresh instance with clean state)
    const manager = createAgentManager({
      client: clientRef.current,
      model: 'gpt-realtime-mini',
      initialAgentId: getInitialAgentId(),
      agents,
      userData: config.userData,
      voiceGender,
      userLocation: config.userLocation,
      useEphemeralTokens: false,
    });
    
    managerRef.current = manager;
    
    // Setup manager event listeners
    setupManagerEvents(manager);
    
    // Start the agent system
    await manager.start();
  }, [setupAudioHandlers]);
  
  /**
   * Setup manager event listeners
   */
  const setupManagerEvents = useCallback((manager: AgentManager) => {
    manager.on('connected', () => {
      logger.connection('[useAgentAPI] Connected');
      setConnected(true);
    });
    
    manager.on('disconnected', () => {
      logger.connection('[useAgentAPI] Disconnected');
      setConnected(false);
    });
    
    manager.on('reconnecting', (agentId: string) => {
      logger.connection('[useAgentAPI] Reconnecting for agent:', agentId);
      setIsTransitioning(true);
      
      // DON'T stop audio here - keep playing until new client is ready
      // This provides seamless audio during instant transitions
      // Audio handlers will be rebound when agent is activated
    });
    
    manager.on('stateChanged', (state: AgentManagerState) => {
      setCurrentAgentId(state.currentAgentId);
      setContext(state.context);
      setIsTransitioning(state.isTransitioning);
    });
    
    manager.on('agentActivated', (agentId: string) => {
      logger.info('[useAgentAPI] Agent activated:', agentId);
      
      // Re-sync client ref and audio handlers after agent activation
      // This is the ONLY place where audio handlers are rebound during transitions
      if (managerRef.current) {
        const newClient = managerRef.current.getClient();
        
        // Only rebind if client actually changed
        if (newClient !== clientRef.current) {
          logger.audio('[useAgentAPI] Rebinding audio handlers to new client');
          clientRef.current = newClient;
          setupAudioHandlers(newClient);
        }
      }
      
      // Mark transition as complete
      setIsTransitioning(false);
    });
    
    manager.on('transitionCompleted', (event) => {
      logger.info('[useAgentAPI] Transition completed:', event.fromAgentId, '->', event.toAgentId);
    });
    
    manager.on('error', (error) => {
      logger.error('[useAgentAPI] Error:', error);
    });
  }, [setupAudioHandlers]);
  
  // Connect - routes to appropriate mode
  const connect = useCallback(async (config: UseAgentAPIConfig) => {
    if (useEphemeralTokens) {
      await connectWithTokens(config);
    } else {
      await connectWithApiKey(config);
    }
  }, [useEphemeralTokens, connectWithTokens, connectWithApiKey]);
  
  // Disconnect
  const disconnect = useCallback(async () => {
    logger.connection('[useAgentAPI] Disconnecting...');
    audioStreamerRef.current?.stop();
    await managerRef.current?.stop();
    setConnected(false);
    setCurrentAgentId(null);
    setContext(null);
  }, []);
  
  // Stop audio
  const stopAudio = useCallback(() => {
    audioStreamerRef.current?.stop();
  }, []);
  
  // Send text message
  const send = useCallback((message: string, turnComplete: boolean = true) => {
    managerRef.current?.send(message, turnComplete);
  }, []);
  
  // Send realtime input
  const sendRealtimeInput = useCallback((chunks: Array<{ mimeType: string; data: string }>) => {
    managerRef.current?.sendRealtimeInput(chunks);
  }, []);
  
  // Update transition data
  const updateTransitionData = useCallback((data: Partial<TransitionData>) => {
    managerRef.current?.updateTransitionData(data);
  }, []);
  
  // Manual transition
  const transitionTo = useCallback(async (
    agentId: string,
    reason: string,
    data?: Partial<TransitionData>
  ): Promise<boolean> => {
    if (!managerRef.current) return false;
    return managerRef.current.transitionTo(agentId, reason, data);
  }, []);
  
  return {
    // State
    connected,
    currentAgentId,
    context,
    volume,
    isTransitioning,
    
    // Methods
    connect,
    disconnect,
    stopAudio,
    send,
    sendRealtimeInput,
    updateTransitionData,
    transitionTo,
    
    // References
    manager: managerRef.current,
    client: clientRef.current,
    audioStreamer: audioStreamerRef.current,
  };
}
